import { Box, Flex, Text, Image } from "rebass/styled-components";
import { useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useStatsigClient } from "@statsig/react-bindings";

import { BaseSection, Container, EarlyAccessSection } from "./styledComponents";
import { TitleAndLoadouts } from "./TitleAndLoadouts";
import { Moment } from "./Moment";
import { Button, Link } from "../Button";
import { TLoadout } from "MVVM/Views/CreateMontage/@types";
import { FinalTouches } from "./FinalTouches";
import { QuickEditViewModel } from "./ViewModel";
import { LoadingSpinner } from "../Lottie";
import theme from "theme/themeThree";
import {
  StyledIcon,
  StyledRemixButton,
} from "MVVM/Views/Studio/RemixView/Components/Pinned";
import {
  QuickEditPreferredLoadout,
  QuickEditPreferredThumbnail,
  QuickEditTitle,
} from "MVVM/State/quickEdit";

import { getMatchHistoryGameEnum } from "MVVM/Views/MatchHistory/utils";
import { getPlayerGameIdByGame } from "MVVM/Views/MatchHistory/utils";
import { useUser } from "MVVM/Hooks/useUser";
import { analytics } from "MVVM/Analytics";
import { IQuickEditMoment } from "./@types";
import {
  ProfileMontageSession,
  defaultState,
} from "MVVM/State/profileMontages";
import { AllstarModalState } from "MVVM/State/modals";
import { ModalType } from "MVVM/@types";
import { usePlaywireAds } from "MVVM/Hooks/usePlaywireAds";
import { ActiveAdAtom, ClipsCreatedAtom } from "MVVM/State/ads";
import { FinalizingContainer } from "./FinalizingContainer";
import { CreatorCreditIcon } from "MVVM/Views/Pricing/Components/Table/CreatorCreditIcon";
import { useClipEligibility } from "MVVM/Hooks/useClipEligibility";
import { usePortal } from "MVVM/Hooks/usePortal";
import { ClipEligibilityModal } from "MVVM/Components/Modals/ClipEligibilityModal";

const TOTAL_STEPS = 2;
const { REACT_APP_S3_CLIP_BUCKET } = process.env;

export type TQuickEditType = "MOMENT" | "MONTAGE";
interface IQuickEditProps {
  type?: TQuickEditType;
  momentId: string;
  closeModal: () => void;
  onCreate?: () => void;
}

export const QuickEdit = ({
  momentId,
  closeModal,
  onCreate,
  type = "MOMENT",
}: IQuickEditProps) => {
  const { allstarUser } = useUser();
  const { getExperiment } = useStatsigClient();
  const [step, setStep] = useState<number>(1);
  const preferredLoadout = useRecoilValue(QuickEditPreferredLoadout);
  const [selectedLoadout, setSelectedLoadout] = useState<TLoadout | null>(
    preferredLoadout,
  );
  const [activeAd, setActiveAd] = useRecoilState(ActiveAdAtom);
  const isMatchHistory = window.location.href.includes("match-history");
  const [thumbnailKey, setThumbnailKey] = useState<string>("");
  const [preferredThumbnail, setPreferredThumbnail] = useRecoilState(
    QuickEditPreferredThumbnail,
  );
  const [title, setTitle] = useRecoilState(QuickEditTitle);
  const [finalizing, setFinalizing] = useState<boolean>(false);
  const [loadingAd, setLoadingAd] = useState<boolean>(false);
  const { checkUserClipEligibility } = useClipEligibility();
  const [canCreateClipAt, setCanCreateClipAt] = useState<string | undefined>(
    undefined,
  );

  const inEssentialsTierExperiment = getExperiment("essentials_tier").get(
    "limit_clip_creation",
    false,
  );

  const {
    portalOpen: eligibilityModalOpen,
    setPortalOpen: setEligibilityModalOpen,
    Portal: EligibilityModalPortal,
  } = usePortal(
    <ClipEligibilityModal
      canCreateClipAt={canCreateClipAt}
      onClose={() => setEligibilityModalOpen(false)}
      closeQuickEditModal={closeModal}
    />,
  );

  const { triggerAd } = usePlaywireAds({
    onComplete: () => onComplete(),
    onShowHiddenContainer: () => {
      setFinalizing(true);
      setLoadingAd(false);
    },
    path: "clip-modal",
  });
  const [selectedFinalTouches, setSelectedFinalTouches] = useState<string[]>(
    [],
  );
  const [isOwnMoment, setIsOwnMoment] = useState<boolean>(false);
  const setClipsCreated = useSetRecoilState(ClipsCreatedAtom);
  const {
    moment,
    momentCreateClip,
    momentCreateMontage,
    mediaUploadUrl,
    loadouts,
    campaignAlert,
    getBalance,
  } = QuickEditViewModel(momentId);
  const [montageSession, setMontageSession] = useRecoilState(
    ProfileMontageSession,
  );
  const [, setAllstarModalState] = useRecoilState(AllstarModalState);
  const onComplete = useCallback(() => {
    setTitle("");
    closeModal();
  }, [setTitle, closeModal]);

  useEffect(() => {
    const relevantPlayerGameId = getPlayerGameIdByGame(
      getMatchHistoryGameEnum(moment?.game),
      allstarUser,
    );
    setIsOwnMoment(relevantPlayerGameId === moment?.player?.gameId);
  }, [moment, allstarUser]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    if (finalizing) {
      timeout = setTimeout(() => {
        onComplete();
      }, 5000);
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [finalizing, onComplete]);

  useEffect(() => {
    analytics.track("Quick Edit Display", { type });
  }, [type]);

  const effectsOverridesGetter = (loadoutId?: string) => {
    return {
      thumbnail: thumbnailKey ?? preferredThumbnail,
      loadout: loadoutId,
      rapid: selectedFinalTouches.includes("rapidFire"),
      contextCameras: selectedFinalTouches.includes("contextCams"),
      applySignatureMusic: selectedFinalTouches.includes("music"),
      applySignatureCards: selectedFinalTouches.includes("signature"),
      disableJumpCuts: selectedFinalTouches.includes("disableJumpCuts"),
      applyMountedCameras: selectedFinalTouches.includes("mountedCameras"),
      applyFinalKillCamera: selectedFinalTouches.includes("finalKillCamera"),
      ...(title && { title }),
    };
  };

  const handleCreateMontage = async (loadoutId?: string) => {
    const { data } = await getBalance();
    if (data?.myLedgerBalance.block) {
      closeModal();
      setAllstarModalState((allstarModalState) => ({
        ...allstarModalState,
        isOpen: ModalType.MicroTransactions,
        data: { type: "montages" },
      }));
      return false;
    }
    analytics.track("Montage Requested", {
      version: 2,
      clips: montageSession.clips?.length,
    });

    analytics.track("Montage Quick Edit Create", {
      game: moment?.game,
      thumbnailUploaded: !!thumbnailKey,
      loadoutSelected: !!selectedLoadout?._id,
      signatureEnabled: selectedFinalTouches.includes("signature"),
      musicEnabled: selectedFinalTouches.includes("music"),
      rapidFireEnabled: selectedFinalTouches.includes("rapidFire"),
      contextCamsEnabled: selectedFinalTouches.includes("contextCams"),
      jumpCuts: !selectedFinalTouches.includes("disableJumpCuts"),
      mountedCameras: selectedFinalTouches.includes("mountedCameras"),
      finalKillCamera: selectedFinalTouches.includes("finalKillCamera"),
      titleEdited: title !== "",
    });

    await momentCreateMontage({
      variables: {
        MomentsCreateMontageInput: {
          ids: montageSession.clips.map((clip) => clip.moment!.id),
          effectsOverrides: effectsOverridesGetter(loadoutId),
        },
      },
    });

    setMontageSession(defaultState);
    setFinalizing(true);
    return true;
  };

  const handleCreateClip = async (
    moment: IQuickEditMoment,
    loadoutId?: string,
  ) => {
    await momentCreateClip({
      variables: {
        momentCreateClipInput: {
          ids: [momentId],
          matchId: moment.match.id,
          effectsOverrides: effectsOverridesGetter(loadoutId),
        },
      },
    });

    analytics.track("Quick Edit Create", {
      thumbnailUploaded: !!thumbnailKey,
      loadoutSelected: !!selectedLoadout?._id,
      signatureEnabled: selectedFinalTouches.includes("signature"),
      musicEnabled: selectedFinalTouches.includes("music"),
      rapidFireEnabled: selectedFinalTouches.includes("rapidFire"),
      contextCamsEnabled: selectedFinalTouches.includes("contextCams"),
      jumpCuts: !selectedFinalTouches.includes("disableJumpCuts"),
      mountedCameras: selectedFinalTouches.includes("mountedCameras"),
      finalKillCamera: selectedFinalTouches.includes("finalKillCamera"),
      titleEdited: title !== "",
    });

    setClipsCreated((prev: number) => {
      const newCount = prev + 1;

      if (!activeAd) {
        setLoadingAd(true);
        triggerAd();
      } else setFinalizing(true);

      return newCount;
    });
  };

  const handleClick = async (moment: IQuickEditMoment) => {
    if (!loadouts) return;
    if (step === TOTAL_STEPS) {
      if (
        type === "MOMENT" &&
        allstarUser?.user?._id &&
        inEssentialsTierExperiment
      ) {
        const action = moment?.clip?.id ? "remix" : "create";
        const eligibility = await checkUserClipEligibility(
          allstarUser.user._id,
          allstarUser.user.createdDate,
          action,
        );

        if (eligibility && !eligibility.isEligible) {
          setCanCreateClipAt(eligibility.canCreateClipAt);
          setEligibilityModalOpen(true);
          return;
        }
      }

      let loadoutId = selectedLoadout?._id;
      if (selectedLoadout?.name === "Surprise Me") {
        const randomLoadout =
          loadouts[Math.floor(Math.random() * loadouts.length)];
        loadoutId = randomLoadout._id;
      }
      if (selectedLoadout?.name === "No Loadout") {
        loadoutId = "NO_LOADOUT";
      }

      if (type === "MOMENT") {
        await handleCreateClip(moment, loadoutId);
        if (!isOwnMoment && isMatchHistory) closeModal();
      } else {
        const success = await handleCreateMontage(loadoutId);
        if (!success) return;
      }

      setPreferredThumbnail(thumbnailKey);

      onCreate?.();
    } else {
      analytics.track("Quick Edit Next", { type });
      setStep(step + 1);
    }
  };

  if (!moment)
    return (
      <Container
        alignItems={"center"}
        justifyContent={"center"}
        minHeight={650}
      >
        <LoadingSpinner height={25} width={25} />
      </Container>
    );
  if (finalizing || loadingAd || activeAd === "clip-modal") {
    return (
      <FinalizingContainer
        finalizing={finalizing}
        loadingAd={loadingAd}
        activeAd={activeAd}
        closeModal={closeModal}
      />
    );
  }

  return (
    <Container minHeight={300} flexDirection={"column"}>
      <BaseSection
        flexDirection={"column"}
        style={{ gap: 12, padding: "24px" }}
        justifyContent={"center"}
        alignItems={"start"}
      >
        <Text variant="text.headingXSmall">
          Customize Your {type === "MOMENT" ? "Clip" : "Montage"}:{" "}
        </Text>
        {type === "MOMENT" && <Moment moment={moment} />}

        {campaignAlert && (
          <Text
            variant="text.labelXSmall"
            color={theme.colors.error}
            style={{
              fontWeight: "normal",
            }}
          >
            {campaignAlert}
          </Text>
        )}
      </BaseSection>
      <BaseSection
        flexDirection={"column"}
        style={{ gap: 36 }}
        justifyContent={"center"}
        alignItems={"start"}
      >
        {step === 1 && (
          <TitleAndLoadouts
            moment={moment}
            selectedLoadout={selectedLoadout}
            setSelectedLoadout={setSelectedLoadout}
            setTitle={setTitle}
            title={title}
            loadouts={loadouts ?? []}
            type={type}
          />
        )}
        {step === 2 && (
          <BaseSection
            flexDirection={"column"}
            style={{ padding: "24px", width: "100%" }}
            justifyContent={"center"}
            alignItems={"start"}
          >
            <FinalTouches
              setThumbnailKey={setThumbnailKey}
              selectedFinalTouches={selectedFinalTouches}
              setSelectedFinalTouches={setSelectedFinalTouches}
              mediaUploadUrl={mediaUploadUrl}
              isOwnMoment={isOwnMoment}
              moment={moment}
            />
          </BaseSection>
        )}
      </BaseSection>
      <Controls>
        <Gradient currentStep={step} totalSteps={TOTAL_STEPS} />
        <Button
          classification="roundedFilledAlt"
          size="small"
          onClick={() => closeModal()}
          flex={true}
        >
          Cancel
        </Button>
        <Flex flexDirection={"row"} style={{ gap: 12 }}>
          {step > 1 && (
            <Button
              classification="roundedFilledAlt"
              size="small"
              onClick={() => setStep(step - 1)}
              flex={true}
            >
              Back
            </Button>
          )}

          {step === TOTAL_STEPS ? (
            <>
              {type === "MONTAGE" ? (
                <Button
                  flex={true}
                  size="tiny"
                  classification="roundedFilled"
                  onClick={() => handleClick(moment)}
                  py={5}
                >
                  Create for <CreatorCreditIcon /> 1
                </Button>
              ) : moment?.clip?.id ? (
                <StyledRemixButton
                  size="small"
                  disabled={finalizing || loadingAd}
                  onClick={() => handleClick(moment)}
                  classification="remixFeed"
                >
                  <StyledIcon />
                  Remix
                </StyledRemixButton>
              ) : (
                <Button
                  classification="roundedFilled"
                  size="small"
                  disabled={finalizing || loadingAd}
                  onClick={() => handleClick(moment)}
                  flex={true}
                >
                  Create
                </Button>
              )}
            </>
          ) : (
            <Button
              classification="roundedFilled"
              size="small"
              disabled={finalizing}
              onClick={() => handleClick(moment)}
              flex={true}
            >
              Next
            </Button>
          )}
        </Flex>
      </Controls>
      {step === 2 && (
        <EarlyAccessSection>
          <Flex flexDirection="row" style={{ gap: 12 }} alignItems="center">
            <Image
              src={`${REACT_APP_S3_CLIP_BUCKET}/static/svg/early-access.svg`}
              height={24}
            />
            <Text variant="text.paragraphMedium">Early Access</Text>
          </Flex>
          <Text color={"chalk"} variant="text.labelSmall">
            This feature is in Early Access to address fan feedback and fix any
            bugs before launch. To get Early Access to new features,{" "}
            <Link variant="link.title" to="/upgrade" style={{ color: "white" }}>
              Subscribe to Allstar!
            </Link>
          </Text>
        </EarlyAccessSection>
      )}
      {eligibilityModalOpen && EligibilityModalPortal}
    </Container>
  );
};

const Gradient = styled(Box)<{ currentStep: number; totalSteps: number }>`
  height: 4px;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  background: ${({ currentStep, totalSteps }) =>
    currentStep === totalSteps
      ? `linear-gradient(to right, ${theme.colors.envy} 50%, ${theme.colors.chalkAlpha20} 50%)`
      : theme.colors.chalkAlpha20};
`;

const Controls = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  max-height: 100px;
  padding: 24px;
  position: relative;
`;
